import {
  DefaultTheme,
  LinkingOptions,
  NavigationContainer,
  createNavigationContainerRef,
} from '@react-navigation/native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useBottomSheet } from 'context/BottomSheetContext';
import { useGlobalConfig } from 'context/GlobalContext';
import { PinProvider } from 'context/PinContext';
import { getDeviceTypeAsync } from 'expo-device';
import { useEnsureProfiles } from 'hooks/useEnsureProfiles';
import { useEffect } from 'react';
import { Text, View } from 'react-native';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { useDispatch, useSelector } from 'react-redux';
import { DOWNLOAD_APP_PATH } from 'screens/DownloadApp';
import ErrorHandler, { ErrorHandlerType } from 'screens/ErrorHandler';
import { RootState } from 'store/index';
import {
  deleteDeepLinkingPath,
  normalizeDeepLinkPath,
  setDeepLinkingPath,
} from 'store/slices/deepLinkingSlice';
import theme from 'theme';
import { Platform } from 'utils/platform';
import { loadWebTVInfo } from 'utils/tv/webTVInfo';

import { AuthStack } from './stacks/AuthStack';
import { MainStack } from './stacks/MainStack';
import { ProfileStack } from './stacks/ProfileStack';

import { AccountDeactivatedGuard } from 'components/AccountDeactivated';
import BandMenuModal from 'components/BandMenuModal/BandMenuModal';
import LiveMenuProvider from 'components/LiveMenu';
import PersistentControl from 'components/Player/Stream/UI/PersistentControl';
import { usePlayer } from 'components/Player/context';
import SidebarMenu from 'components/SidebarMenu';
import { WebDiv } from 'components/WebDiv';

export type RootStackParamList = {
  DownloadApp: undefined;
  Media: undefined;
  Search: undefined;
  Favorites: undefined;
  ContactUs: undefined;
  FaqSection: undefined;
  LegalTerms: undefined;
  VideoPlayer: undefined;
  Category: { slug: string };
  Login: { redirect?: string } | undefined;
  ProfileSelection: undefined;
  ProfileCreate: undefined;
  ProfileEdit: { profileId: string };
  Program: { programId: string; mediaId?: string; seasonId?: string };
  Home: undefined;
  Listen: { id: string };
  Videos: { id: string };
  Generic: { id: string };
  MyProfile: undefined;
  EasterEgg: undefined;
  NotFound: { path?: string } | undefined;
  Analytics: undefined;
  TvSubMenuArea: undefined;
  WebView: undefined;
  Onboarding: undefined;
};

export type RootStackScreenProps<T extends keyof RootStackParamList> = NativeStackScreenProps<
  RootStackParamList,
  T
>;

const linking: LinkingOptions<RootStackParamList> = {
  prefixes: ['bandplay://', 'https://www.bandplay.com'],
  config: {
    screens: {
      Home: '',
      Listen: 'ouca',
      Videos: 'assista',
      Generic: ':id/h',
      Search: 'buscar',
      Favorites: 'favoritos',
      Login: 'entrar',
      ...(!Platform.isTV && { DownloadApp: DOWNLOAD_APP_PATH }),
      ProfileSelection: 'profiles',
      ProfileCreate: 'profiles/create',
      ProfileEdit: 'profiles/edit/:profileId',
      Program: ':programId/:seasonId?/:episodeName?/:mediaId?',
      Category: 'categorias/:slug',
      FaqSection: 'ajuda',
      LegalTerms: 'termos',
      TvSubMenuArea: 'tvSubMenuArea',
      ContactUs: 'contato',
      MyProfile: 'perfil',
      EasterEgg: 'easter-egg',
      Analytics: 'analytics',
      Onboarding: 'onboarding',
      NotFound: '*',
    },
  },
};

export const navigationRef = createNavigationContainerRef<any>();

const ENVIRONMENTS_DISPLAY = ['ved4', 'ved5', 'ved6', 'ved7', 'ved8'];
const getCurrentVedEnvironment = () => {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return undefined;

  const hostname = window.location.hostname.toLowerCase();
  if (hostname === 'localhost') return 'local';
  return ENVIRONMENTS_DISPLAY.find((environment) => hostname.includes(environment))?.toUpperCase();
};

export const AppNavigator = () => {
  useEnsureProfiles();
  const { setColor, hasInternet } = useGlobalConfig();
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  // const userDeviceData = useSelector((state: RootState) => state.userDeviceData);
  const {
    setError,
    media,
    reloadMedia,
    pause,
    isFullScreen,
    // hasTransmissionError,
    setHasTransmissionError,
  } = usePlayer();
  const { handleToggleSheet, visible } = useBottomSheet();
  const deepLinkingData = useSelector((state: RootState) => state.deepLinking);
  const dispatch = useDispatch();

  const currentVedEnvironment = getCurrentVedEnvironment();

  // const hasBottomMenu =
  //   !isFullScreen &&
  //   !Platform.isTV &&
  //   (Platform.OS === 'web' || userDeviceData.hasCompletedOnboarding);

  const selectedProfileId = useSelector((state: RootState) => state.profile.selectedProfileId);

  useEffect(() => {
    if (Platform.OS === 'web') {
      const cleanPath = window.location.pathname;

      if (cleanPath.includes('/profiles') || cleanPath.includes(`/${DOWNLOAD_APP_PATH}`)) return;

      const urlTo = cleanPath.startsWith('/') ? cleanPath.slice(1) : cleanPath;

      if (urlTo && urlTo !== '') {
        dispatch(setDeepLinkingPath({ path: urlTo }));
      }
    }
  }, [dispatch]);

  useEffect(() => {
    if (Platform.OS === 'web' && !Platform.isTV) {
      const { path } = deepLinkingData;
      const cleanPath = window.location.pathname;

      if (!path) return;

      if (!isAuthenticated || !selectedProfileId) return;

      const currentWebPath = normalizeDeepLinkPath(cleanPath.replace(/^\/+/, ''));
      if (currentWebPath === path) {
        dispatch(deleteDeepLinkingPath());
        return;
      }

      dispatch(deleteDeepLinkingPath());

      getDeviceTypeAsync().then(async (deviceType: any) => {
        if (deviceType === 1 || deviceType === 2) {
          if (Platform.isSafari && (!path || path.trim() === '')) return;

          window.location.href = `bandplay://${path}`;

          setTimeout(() => {
            if (!document.hidden) {
              window.location.href = `/${path}`;
            }
          }, 1500);
        } else {
          window.location.href = `/${path}`;
        }
      });
    }
  }, [isAuthenticated, selectedProfileId, deepLinkingData, dispatch]);

  useEffect(() => {
    if (Platform.isWebTV) {
      loadWebTVInfo();
    }
  }, []);

  useEffect(() => {
    setHasTransmissionError(!hasInternet);

    if (hasInternet) {
      setError(undefined);
      if (media !== undefined) reloadMedia();
    } else {
      if (media !== undefined) {
        pause();
      }
    }
  }, [hasInternet]);

  useEffect(() => {
    isFullScreen && visible && handleToggleSheet();
  }, [isFullScreen]);

  // const shouldSkipAuthentication = dataLogin?.skipAuthenticated;

  const isPublicRoute =
    Platform.OS === 'web' &&
    !Platform.isTV &&
    typeof window !== 'undefined' &&
    (window.location.pathname.includes('/termos') ||
      window.location.pathname.includes(`/${DOWNLOAD_APP_PATH}`));

  return (
    <SafeAreaProvider style={{ backgroundColor: theme.colors.base.darkBlue }}>
      <LiveMenuProvider navigationRef={navigationRef}>
        <NavigationContainer
          ref={navigationRef}
          linking={linking}
          theme={{
            ...DefaultTheme,
            colors: { ...DefaultTheme.colors, background: theme.colors.base.darkBlue },
          }}
          onStateChange={(e) => {
            const lastStack = e?.routes[e.routes.length - 1];
            if (lastStack?.name !== 'Program') {
              setColor(undefined);
            }
            visible && handleToggleSheet();
          }}>
          {currentVedEnvironment && Platform.isTV && (
            <View
              style={{ position: 'absolute', top: 20, right: 30, zIndex: 99999 }}
              pointerEvents="none">
              <Text
                style={{
                  fontSize: 32,
                  lineHeight: 38,
                  fontWeight: '900',
                  color: '#eaeaea',
                  textShadowColor: 'rgba(0, 0, 0, 0.8)',
                  textShadowOffset: { width: 2, height: 2 },
                  textShadowRadius: 3,
                }}>
                ENV: {currentVedEnvironment}
              </Text>
            </View>
          )}
          <View
            style={{
              zIndex: !hasInternet ? 9999 : -1,
              position: 'absolute',
              top: 0,
              left: !hasInternet ? 0 : -9999999,
              right: 0,
              bottom: 0,
            }}>
            <ErrorHandler error={ErrorHandlerType.NO_INTERNET} />
          </View>
          <PinProvider>
            <View style={{ flex: 1, flexDirection: 'row' }}>
              {Platform.isTV && isAuthenticated && selectedProfileId && hasInternet && (
                <SidebarMenu />
              )}
              <WebDiv style={{ flex: 1, display: 'flex' }} className="navigation-container">
                {!isAuthenticated ? (
                  <AuthStack />
                ) : !selectedProfileId && !isPublicRoute ? (
                  <ProfileStack />
                ) : (
                  <MainStack />
                )}
              </WebDiv>
            </View>
            {/* {hasBottomMenu && !hasTransmissionError && <BottomMenu navigationRef={navigationRef} />} */}
            <PersistentControl navigationRef={navigationRef} />
            <AccountDeactivatedGuard />
          </PinProvider>
        </NavigationContainer>
      </LiveMenuProvider>
    </SafeAreaProvider>
  );
};
